import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { FieldLabel } from '../../shared/ui/field-label';
import { Input } from '../../shared/ui/input';
import { Modal } from '../../shared/ui/modal';
import { MutationError } from '../../shared/ui/mutation-error';
import {
  OLDER_DATA,
  deleteNow,
  formatBytes,
  ownersText,
  type TDeletePreview,
} from '../../entities/retention';

const SHOWN = 4;

interface IDeleteConfirmProps {
  preview: TDeletePreview;
  organizationName: string;
  onStarted: (operationId: string) => void;
  onClose: () => void;
}

const flowsWord = (n: number) => `${n.toLocaleString('en-US')} ${n === 1 ? 'flow' : 'flows'}`;

/** Board S2: the confirmation names the size and the flows, and wants the name typed. */
export const DeleteConfirm = ({
  preview,
  organizationName,
  onStarted,
  onClose,
}: IDeleteConfirmProps) => {
  const [typed, setTyped] = useState('');
  const start = useMutation({
    mutationFn: () =>
      deleteNow({
        scope: preview.scope,
        olderThanDays: preview.olderThanDays,
        confirmName: typed,
      }),
    onSuccess: (operation) => onStarted(operation.id),
  });
  const flows = preview.owners.filter((owner) => owner.kind === 'flow');
  const shown = flows.slice(0, SHOWN);
  const rest = flows.slice(SHOWN);
  const others = preview.bytes - flows.reduce((sum, owner) => sum + owner.bytes, 0);
  const days = `${preview.olderThanDays.toLocaleString('en-US')} ${preview.olderThanDays === 1 ? 'day' : 'days'}`;
  const size = `~${formatBytes(preview.bytes)}`;

  return (
    <Modal
      open
      width="md"
      onOpenChange={(open) => !open && onClose()}
      title={`Delete ${size} now?`}
      description={
        preview.scope === 'content'
          ? `Webhook bodies, responses and step results older than ${days}. History stays: runs still show in Events, without their content.`
          : `Everything older than ${days}: content and history. Those runs disappear from Events and their traces.`
      }
      submit={{
        label: `Delete ${size} from ${ownersText(preview.owners)}`,
        onSubmit: () => start.mutate(),
        disabled: typed.trim() !== organizationName.trim(),
        waiting: start.isPending,
        waitingLabel: 'Starting',
        destructive: true,
      }}
    >
      <p className="font-mono text-micro text-faint">From {ownersText(preview.owners)}</p>
      <div className="mt-2 flex flex-col gap-label">
        {shown.map((owner) => (
          <div key={owner.id} className="flex justify-between gap-3 text-meta">
            <span className="truncate text-muted">{owner.name ?? 'A deleted flow'}</span>
            <span className="font-mono text-primary">{formatBytes(owner.bytes)}</span>
          </div>
        ))}
        {rest.length > 0 && (
          <div className="flex justify-between gap-3 text-meta">
            <span className="text-muted">{flowsWord(rest.length)} more</span>
            <span className="font-mono text-primary">
              {formatBytes(rest.reduce((sum, owner) => sum + owner.bytes, 0))}
            </span>
          </div>
        )}
        {others > 0 && (
          <div className="flex justify-between gap-3 text-meta">
            <span className="text-muted">{OLDER_DATA}</span>
            <span className="font-mono text-primary">{formatBytes(others)}</span>
          </div>
        )}
      </div>
      <p className="mt-3 text-meta text-muted">
        Not touched: {preview.keptUnfinished.toLocaleString('en-US')}{' '}
        {preview.keptUnfinished === 1 ? 'copy' : 'copies'} still being delivered,{' '}
        {preview.keptBookmarked.toLocaleString('en-US')} bookmarked.
      </p>
      <div className="mt-3">
        <FieldLabel htmlFor="delete-confirm-name">Type the organization name to confirm</FieldLabel>
        <Input
          id="delete-confirm-name"
          autoComplete="off"
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
        />
      </div>
      <MutationError error={start.error} />
    </Modal>
  );
};
